import { Vector2 } from '@shared/types';
import { Enemy, EnemyRarity } from '@client/entities/Enemy';
import { Player } from '@client/entities/Player';

export interface TargetScore {
  enemy: Enemy;
  score: number;
  distance: number;
  angle: number;
}

export class TargetingSystem {
  // Weights for scoring
  private static DISTANCE_WEIGHT = 1.0;
  private static ANGLE_WEIGHT = 0.8;
  private static RARITY_WEIGHT = 0.3;

  /**
   * Find the best target for auto-aim
   * Considers: direction, distance, and enemy importance
   */
  static findBestTarget(
    player: Player,
    enemies: Enemy[],
    maxRange: number = 300,
    facingDirection?: Vector2
  ): Enemy | null {
    if (enemies.length === 0) return null;

    // Default facing direction based on player
    const direction = facingDirection || player.getFacingVector();

    const scores: TargetScore[] = [];
    const maxRangeSq = maxRange * maxRange;

    for (const enemy of enemies) {
      if (!enemy.isAlive) continue;

      // Calculate distance (early squared check to skip sqrt for out-of-range)
      const dx = enemy.position.x - player.position.x;
      const dy = enemy.position.y - player.position.y;
      const distSq = dx * dx + dy * dy;

      // Skip if too far
      if (distSq > maxRangeSq) continue;
      const distance = Math.sqrt(distSq);

      // Calculate angle difference from facing direction
      const toEnemyX = dx / distance;
      const toEnemyY = dy / distance;
      const dot = direction.x * toEnemyX + direction.y * toEnemyY;
      const angle = Math.acos(Math.max(-1, Math.min(1, dot))); // 0 = same direction, PI = opposite

      // Skip if enemy is behind (more than 90 degrees off)
      if (angle > Math.PI / 2) continue;

      // Calculate score (higher = better target)
      const distanceScore = 1 - (distance / maxRange); // Closer = higher
      const angleScore = 1 - (angle / (Math.PI / 2)); // More aligned = higher
      const rarityScore = this.getEnemyRarityScore(enemy);

      const totalScore =
        distanceScore * this.DISTANCE_WEIGHT +
        angleScore * this.ANGLE_WEIGHT +
        rarityScore * this.RARITY_WEIGHT;

      scores.push({
        enemy,
        score: totalScore,
        distance,
        angle,
      });
    }

    if (scores.length === 0) return null;

    // Sort by score (highest first)
    scores.sort((a, b) => b.score - a.score);

    return scores[0].enemy;
  }

  /**
   * Get all valid targets in range, sorted by distance
   */
  private static _targetBuf: { enemy: Enemy; distSq: number }[] = [];
  static getTargetsInRange(
    player: Player,
    enemies: Enemy[],
    maxRange: number = 300
  ): Enemy[] {
    const maxRangeSq = maxRange * maxRange;
    const px = player.position.x;
    const py = player.position.y;
    const buf = this._targetBuf;
    buf.length = 0;

    for (const enemy of enemies) {
      if (!enemy.isAlive) continue;
      const dx = enemy.position.x - px;
      const dy = enemy.position.y - py;
      const distSq = dx * dx + dy * dy;
      if (distSq <= maxRangeSq) {
        buf.push({ enemy, distSq });
      }
    }

    buf.sort((a, b) => a.distSq - b.distSq);
    const result: Enemy[] = [];
    for (let i = 0; i < buf.length; i++) {
      result.push(buf[i].enemy);
    }
    return result;
  }

  /**
   * Get the position to aim at for a target
   * Leads the target slightly based on movement
   */
  static getAimPosition(enemy: Enemy, _leadTime: number = 0.2): Vector2 {
    // For now just return enemy position
    // Could add prediction based on enemy velocity
    return {
      x: enemy.position.x,
      y: enemy.position.y - enemy.height / 2, // Aim at center mass
    };
  }

  /**
   * Get rarity score for targeting priority
   * Higher rarity enemies might be priority targets
   */
  private static getEnemyRarityScore(enemy: Enemy): number {
    // Check if enemy has rarity property
    const rarity = (enemy as Enemy & { rarity?: EnemyRarity }).rarity;

    switch (rarity) {
      case 'boss':
        return 1.0; // Highest priority
      case 'elite':
        return 0.8; // High priority
      case 'normal':
      default:
        return 0.5; // Normal priority
    }
  }

  /**
   * Find closest enemy regardless of direction (squared distance)
   */
  static findClosestEnemy(
    player: Player,
    enemies: Enemy[],
    maxRange: number = 300
  ): Enemy | null {
    let closest: Enemy | null = null;
    let closestDistSq = maxRange * maxRange;

    for (const enemy of enemies) {
      if (!enemy.isAlive) continue;

      const dx = enemy.position.x - player.position.x;
      const dy = enemy.position.y - player.position.y;
      const distSq = dx * dx + dy * dy;

      if (distSq < closestDistSq) {
        closestDistSq = distSq;
        closest = enemy;
      }
    }

    return closest;
  }

  /**
   * Find enemy closest to a point (for AoE targeting, squared distance)
   */
  static findEnemyNearPoint(
    point: Vector2,
    enemies: Enemy[],
    maxRange: number = 100
  ): Enemy | null {
    let closest: Enemy | null = null;
    let closestDistSq = maxRange * maxRange;

    for (const enemy of enemies) {
      if (!enemy.isAlive) continue;

      const dx = enemy.position.x - point.x;
      const dy = enemy.position.y - point.y;
      const distSq = dx * dx + dy * dy;

      if (distSq < closestDistSq) {
        closestDistSq = distSq;
        closest = enemy;
      }
    }

    return closest;
  }
}
